import { useState, useMemo, useCallback, useEffect } from 'react'
import { useQueries } from '@tanstack/react-query'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import {
  ArrowLeft,
  Download,
  RefreshCw,
  FileText,
  Users,
  ShieldCheck,
  Stethoscope,
  MapPin,
  Calendar,
  Plus,
  Clock,
  Zap,
} from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/components/ui/card'
import { Button } from '@/shared/components/ui/button'
import { Skeleton } from '@/shared/components/ui/skeleton'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu'
import { useConfiguracion } from '@/modules/registros/hooks/useConfiguraciones'
import {
  useEstadisticasReporte,
  useDatosGraficos,
  useRegistros,
} from '@/modules/registros/hooks/useReporteData'
import { registrosApi } from '@/modules/registros/api/registros.api'
import { useBreadcrumb } from '@/shared/context/BreadcrumbContext'
import {
  MetricCard,
  FilterPanel,
  RegistrosPorFechaChart,
  RegistrosTable,
  DynamicMetricCard,
  DynamicVerticalBarChart,
  DynamicHorizontalBarChart,
  DynamicLineChart,
  HitosTimeline,
} from '../components'
import type { VisualizacionConfig, GraficaBarrasItemConfig, GraficaLineasItemConfig } from '@/modules/registros/types'

// Helper para migrar configuración antigua a nueva
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function migrateVisualizacionConfig(config: Record<string, any> | null | undefined): VisualizacionConfig | null {
  if (!config) return null

  // Si tiene formato antiguo (grafica_barras como objeto) y no tiene formato nuevo
  if (config.grafica_barras && !config.graficas_verticales) {
    const oldConfig = config.grafica_barras
    return {
      cards: config.cards || { habilitado: false, cards: [] },
      graficas_verticales: {
        habilitado: oldConfig.habilitado,
        graficas: oldConfig.habilitado && oldConfig.columna_agrupacion ? [{
          id: 'migrated-1',
          columna_agrupacion: oldConfig.columna_agrupacion,
          titulo: oldConfig.titulo || 'Distribución',
          color: oldConfig.color || '#2fbc80',
          mostrar_etiquetas: oldConfig.mostrar_etiquetas ?? true,
          limite_categorias: oldConfig.limite_categorias ?? 10,
          orden: 0,
        }] : [],
      },
      graficas_horizontales: {
        habilitado: false,
        graficas: [],
      },
      graficas_lineas: {
        habilitado: false,
        graficas: [],
      },
    }
  }

  // Ya tiene formato nuevo o no necesita migración
  return {
    cards: config.cards || { habilitado: false, cards: [] },
    graficas_verticales: config.graficas_verticales || { habilitado: false, graficas: [] },
    graficas_horizontales: config.graficas_horizontales || { habilitado: false, graficas: [] },
    graficas_lineas: config.graficas_lineas || { habilitado: false, graficas: [] },
  }
}

export function ReporteDetallePage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const { setOverride, clearOverride } = useBreadcrumb()
  const configId = Number(id)

  // Estado de filtros
  const [activeFilters, setActiveFilters] = useState<Record<string, string[]>>({})
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)
  const [sortColumn, setSortColumn] = useState<string>()
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')
  const [searchTerm, setSearchTerm] = useState('')
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date())

  // Convertir filtros activos a formato de API
  const apiFilters = useMemo(() => {
    const filters: Record<string, string> = {}
    Object.entries(activeFilters).forEach(([key, values]) => {
      if (values.length > 0) {
        filters[key] = values.join(',')
      }
    })
    return filters
  }, [activeFilters])

  // Queries
  const {
    data: configuracion,
    isLoading: configLoading,
    error: configError,
  } = useConfiguracion(configId)

  const {
    data: estadisticas,
    isLoading: statsLoading,
    refetch: refetchStats,
  } = useEstadisticasReporte(configId, apiFilters)

  const {
    data: datosGraficos,
    isLoading: graficosLoading,
    refetch: refetchGraficos,
  } = useDatosGraficos(configId, apiFilters)

  const {
    data: registrosData,
    isLoading: registrosLoading,
    refetch: refetchRegistros,
  } = useRegistros(configId, {
    page,
    page_size: pageSize,
    ordenar_por: sortColumn ? `${sortDirection === 'desc' ? '-' : ''}${sortColumn}` : undefined,
    filtros: apiFilters,
    busqueda: searchTerm || undefined,
  })

  // Cargar valores de filtro para cada campo filtrable
  const camposFiltros = configuracion?.campos_filtros || []

  // Fetch filter values for each campo
  const filterValuesQueries = useQueries({
    queries: camposFiltros.map((campo) => ({
      queryKey: ['reporte-valores-filtro', configId, campo],
      queryFn: () => registrosApi.getValoresFiltro(configId, campo),
      enabled: !!configId && !!campo,
    })),
  })

  // Build a map of campo -> options
  const filterValuesMap = useMemo(() => {
    const map: Record<string, Array<{ value: string; label: string; count?: number }>> = {}
    camposFiltros.forEach((campo, index) => {
      const query = filterValuesQueries[index]
      if (query?.data?.valores && Array.isArray(query.data.valores)) {
        // Backend returns List[{valor: string, count: number}]
        // '__NULL__' es el centinela para registros con valor NULL/vacío
        map[campo] = query.data.valores.map((item: { valor: string; count: number }) => ({
          value: item.valor,
          label: item.valor === '__NULL__' ? `Pendiente (${item.count})` : `${item.valor} (${item.count})`,
          count: item.count,
        }))
      } else {
        map[campo] = []
      }
    })
    return map
  }, [camposFiltros, filterValuesQueries])

  // Columnas que deben mostrar "Pendiente" en lugar de "-" cuando el valor es null/vacío
  const nullLabels = useMemo(() => {
    const labels: Record<string, string> = {}
    camposFiltros.forEach((campo) => {
      const opciones = filterValuesMap[campo] || []
      if (opciones.some((o) => o.value === '__NULL__')) {
        labels[campo] = 'Pendiente'
      }
    })
    return labels
  }, [camposFiltros, filterValuesMap])

  // Set breadcrumb override when report name is loaded
  useEffect(() => {
    if (configuracion?.titulo_reporte) {
      setOverride(location.pathname, configuracion.titulo_reporte)
    }
    return () => {
      clearOverride(location.pathname)
    }
  }, [configuracion?.titulo_reporte, location.pathname, setOverride, clearOverride])

  // Migrar configuración de visualización
  const visualizacionConfig = useMemo(() => {
    return migrateVisualizacionConfig(configuracion?.visualizacion_config)
  }, [configuracion?.visualizacion_config])

  const isLoading = configLoading || statsLoading

  const handleRefresh = useCallback(() => {
    refetchStats()
    refetchGraficos()
    refetchRegistros()
    setLastUpdate(new Date())
  }, [refetchStats, refetchGraficos, refetchRegistros])

  const handleFilterChange = useCallback((campo: string, values: string[]) => {
    setActiveFilters((prev) => ({
      ...prev,
      [campo]: values,
    }))
    setPage(1) // Reset a primera página al cambiar filtros
  }, [])

  const handleClearFilters = useCallback(() => {
    setActiveFilters({})
    setPage(1)
  }, [])

  const handleExport = useCallback(
    async (formato: 'csv' | 'excel') => {
      try {
        const blob = await registrosApi.exportarRegistros(configId, formato, apiFilters)
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `registros_${configuracion?.grupo_nombre || configId}.${formato === 'excel' ? 'xlsx' : 'csv'}`
        document.body.appendChild(a)
        a.click()
        window.URL.revokeObjectURL(url)
        document.body.removeChild(a)
      } catch (error) {
        console.error('Error al exportar:', error)
      }
    },
    [configId, apiFilters, configuracion?.grupo_nombre]
  )

  const handleSort = useCallback((column: string, direction: 'asc' | 'desc') => {
    setSortColumn(column)
    setSortDirection(direction)
  }, [])

  const handleSearch = useCallback((term: string) => {
    setSearchTerm(term)
    setPage(1) // Reiniciar a página 1 al buscar
  }, [])

  // Preparar configuración de filtros
  const filterConfigs = useMemo(() => {
    if (!camposFiltros.length) return []

    const iconMap: Record<string, React.ReactNode> = {
      especialidad: <Stethoscope className="h-4 w-4" />,
      ciudad: <MapPin className="h-4 w-4" />,
      fecha_registro: <Calendar className="h-4 w-4" />,
    }

    const labelMap: Record<string, string> = {
      especialidad: 'Especialidades',
      ciudad: 'Ciudades',
      fecha_registro: 'Período',
    }

    return camposFiltros.map((campo) => ({
      campo,
      label: configuracion?.nombres_columnas?.[campo] || labelMap[campo] || campo,
      icon: iconMap[campo],
      options: filterValuesMap[campo] || [],
      placeholder: `Todos`,
    }))
  }, [camposFiltros, filterValuesMap, configuracion?.nombres_columnas])

  // Combinar todas las gráficas (verticales, horizontales y líneas) en una sola lista ordenada
  const todasLasGraficas = useMemo(() => {
    const graficas: Array<{
      tipo: 'vertical' | 'horizontal' | 'linea'
      config: GraficaBarrasItemConfig | GraficaLineasItemConfig
    }> = []

    if (visualizacionConfig?.graficas_verticales?.habilitado) {
      visualizacionConfig.graficas_verticales.graficas.forEach((g) => {
        graficas.push({ tipo: 'vertical', config: g })
      })
    }

    if (visualizacionConfig?.graficas_horizontales?.habilitado) {
      visualizacionConfig.graficas_horizontales.graficas.forEach((g) => {
        graficas.push({ tipo: 'horizontal', config: g })
      })
    }

    if (visualizacionConfig?.graficas_lineas?.habilitado) {
      visualizacionConfig.graficas_lineas.graficas.forEach((g) => {
        graficas.push({ tipo: 'linea', config: g })
      })
    }

    // Ordenar por el campo 'orden'
    return graficas.sort((a, b) => a.config.orden - b.config.orden)
  }, [visualizacionConfig])

  // Error state
  if (configError) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-destructive">Error al cargar reporte</CardTitle>
            <CardDescription>No se pudo cargar la configuración del reporte</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <p className="text-sm text-muted-foreground">
              {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
              {(configError as Record<string, any>)?.response?.data?.error || 'Error desconocido'}
            </p>
            <Button
              onClick={() => navigate('/registros/reportes')}
              className="bg-teal-800 hover:bg-teal-900 text-white"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Volver a Reportes
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-3 w-full max-w-full overflow-x-hidden">
      {/* Header compacto */}
      <div className="flex items-center justify-between gap-2 w-full">
        {isLoading ? (
          <Skeleton className="h-6 w-48" />
        ) : (
          <div className="flex items-center gap-3 min-w-0">
            <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={() => navigate('/registros/reportes')}>
              <ArrowLeft className="h-3.5 w-3.5" />
            </Button>
            <h1 className="text-lg font-semibold truncate flex items-center gap-1.5">
              <FileText className="h-4 w-4 shrink-0" />
              <span className="truncate">{configuracion?.titulo_reporte || 'Reporte'}</span>
            </h1>
            {/* Card de última actualización */}
            <div className="hidden sm:flex items-center gap-1.5 px-2.5 py-1 rounded-md border-[0.5px] border-teal-500 text-xs text-muted-foreground shrink-0">
              <Clock className="h-3 w-3" />
              <span className="font-medium text-foreground">Última actualización:</span>
              <span>
                {lastUpdate.toLocaleDateString('es-EC', { day: '2-digit', month: '2-digit', year: 'numeric' })} {lastUpdate.toLocaleTimeString('es-EC', { hour: '2-digit', minute: '2-digit', hour12: true })}
              </span>
            </div>
          </div>
        )}
        <div className="flex items-center gap-1.5 shrink-0">
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={handleRefresh}>
            <RefreshCw className="h-3.5 w-3.5" />
          </Button>
          {configuracion?.asistencia_presencial && (
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs px-2 border-green-500 text-green-600 hover:bg-green-50 dark:border-green-400 dark:text-green-400 dark:hover:bg-green-950"
              onClick={() => navigate(`/registros/asistencias/${configId}`)}
            >
              <Zap className="h-3 w-3 mr-1" />
              Asistencias
            </Button>
          )}
          {configuracion?.url_registro && (
            <Button
              size="sm"
              className="h-7 text-xs px-2 bg-teal-800 hover:bg-teal-900 text-white"
              onClick={() => window.open(configuracion.url_registro ?? undefined, '_blank')}
            >
              <Plus className="h-3 w-3 mr-1" />
              Nuevo
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-7 text-xs px-2">
                <Download className="h-3 w-3 mr-1" />
                Exportar
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleExport('csv')} className="text-xs">CSV</DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('excel')} className="text-xs">Excel</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Métricas en grid responsivo */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 w-full">
        {/* Cards dinámicas desde visualizacion_config */}
        {visualizacionConfig?.cards?.habilitado &&
          visualizacionConfig.cards.cards
            .sort((a, b) => a.orden - b.orden)
            .map((cardConfig) => (
              <DynamicMetricCard
                key={cardConfig.id}
                config={cardConfig}
                configId={configId}
              />
            ))}

        {/* Cards estáticas de estadísticas (si no hay cards dinámicas) */}
        {(!visualizacionConfig?.cards?.habilitado ||
          visualizacionConfig?.cards?.cards?.length === 0) && (
          <>
            {statsLoading ? (
              Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-24" />)
            ) : estadisticas ? (
              <>
                <MetricCard
                  title="Total Registros"
                  value={estadisticas.total_registros}
                  subtitle="Registros en el sistema"
                  icon={Users}
                />
                {estadisticas.politicas_aceptadas !== undefined && (
                  <MetricCard
                    title="Políticas Aceptadas"
                    value={estadisticas.politicas_aceptadas}
                    subtitle={`${estadisticas.porcentaje_politicas?.toFixed(0) || 0}% del total`}
                    icon={ShieldCheck}
                  />
                )}
                <MetricCard
                  title={estadisticas.especialidad_label || 'Especialidades'}
                  value={estadisticas.distinct_especialidad || 0}
                  subtitle="Especialidades distintas"
                  icon={Stethoscope}
                />
                <MetricCard
                  title={estadisticas.ciudad_label || 'Ciudades'}
                  value={estadisticas.distinct_ciudad || 0}
                  subtitle="Ciudades distintas"
                  icon={MapPin}
                />
              </>
            ) : null}
          </>
        )}
      </div>

      {/* Filtros */}
      {filterConfigs.length > 0 && (
        <FilterPanel
          filters={filterConfigs}
          activeFilters={activeFilters}
          onFilterChange={handleFilterChange}
          onClearFilters={handleClearFilters}
          isLoading={isLoading}
        />
      )}

      {/* Fila 1: gráficas de barras (verticales + horizontales) en 2-col */}
      {todasLasGraficas.filter((g) => g.tipo === 'vertical' || g.tipo === 'horizontal').length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full auto-rows-fr">
          {todasLasGraficas
            .filter((g) => g.tipo === 'vertical' || g.tipo === 'horizontal')
            .map((grafica) =>
              grafica.tipo === 'vertical' ? (
                <DynamicVerticalBarChart
                  key={grafica.config.id}
                  config={grafica.config as GraficaBarrasItemConfig}
                  configId={configId}
                />
              ) : (
                <DynamicHorizontalBarChart
                  key={grafica.config.id}
                  config={grafica.config as GraficaBarrasItemConfig}
                  configId={configId}
                />
              ),
            )}
        </div>
      )}

      {/* Fila 2: gráfica lineal (Registros por Fecha) ocupando todo el ancho */}
      <div className="w-full">
        <RegistrosPorFechaChart
          data={datosGraficos?.registros_por_fecha || []}
          marcadores={datosGraficos?.marcadores_especiales || []}
          agrupacion={datosGraficos?.agrupacion_fecha || 'dia'}
          isLoading={graficosLoading}
        />
      </div>

      {/* Fila 3: gráficas de líneas extra desde visualizacion_config (si hay) */}
      {todasLasGraficas.filter((g) => g.tipo === 'linea').length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full auto-rows-fr">
          {todasLasGraficas
            .filter((g) => g.tipo === 'linea')
            .map((grafica) => (
              <DynamicLineChart
                key={grafica.config.id}
                config={grafica.config as GraficaLineasItemConfig}
                configId={configId}
              />
            ))}
        </div>
      )}

      {/* Timeline de Hitos */}
      {datosGraficos?.marcadores_especiales && datosGraficos.marcadores_especiales.length > 0 && (
        <div className="w-full">
          <HitosTimeline marcadores={datosGraficos.marcadores_especiales} />
        </div>
      )}

      {/* Tabla de Registros */}
      <RegistrosTable
        data={registrosData?.results || []}
        columns={configuracion?.campos_mostrar || []}
        nullLabels={nullLabels}
        columnLabels={
          configuracion?.campos_mapeo
            ? Object.fromEntries(
                Object.entries(configuracion.campos_mapeo).map(([key]) => [
                  key,
                  configuracion.nombres_columnas?.[key] ||
                    key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' '),
                ])
              )
            : {}
        }
        totalRegistros={registrosData?.count || 0}
        page={page}
        pageSize={pageSize}
        totalPages={registrosData?.total_pages || 1}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size)
          setPage(1)
        }}
        onSort={handleSort}
        onSearch={handleSearch}
        searchTerm={searchTerm}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        isLoading={registrosLoading}
      />
    </div>
  )
}
